<script setup lang="ts">
import { computed, nextTick, onMounted, watch } from "vue";

import SiteFooter from "./components/SiteFooter.vue";
import SiteHeader from "./components/SiteHeader.vue";
import HomePage from "./pages/HomePage.vue";
import LegalPage from "./pages/LegalPage.vue";
import NotFoundPage from "./pages/NotFoundPage.vue";
import { runtimeConfig } from "./api/config";
import { installLinkInterception, useRoute } from "./router";
import { canonicalPath } from "./routes";

const route = useRoute();

const currentComponent = computed(() => {
  switch (route.value.name) {
    case "home": return HomePage;
    case "legal":
    case "privacy":
    case "terms":
    case "refund": return LegalPage;
    default: return NotFoundPage;
  }
});

const routeProps = computed(() => (
  ["legal", "privacy", "terms", "refund"].includes(route.value.name)
    ? { route: route.value }
    : {}
));

function updateMetadata(): void {
  if (typeof document === "undefined") return;
  document.title = route.value.title;
  document.querySelector<HTMLMetaElement>('meta[name="description"]')?.setAttribute("content", route.value.description);
  document.querySelector<HTMLMetaElement>('meta[property="og:title"]')?.setAttribute("content", route.value.title);
  document.querySelector<HTMLMetaElement>('meta[property="og:description"]')?.setAttribute("content", route.value.description);
  const publicPath = canonicalPath(route.value);
  document.querySelector<HTMLLinkElement>('link[rel="canonical"]')?.setAttribute("href", `${runtimeConfig.publicSiteUrl}${publicPath}`);
  document.querySelector<HTMLMetaElement>('meta[name="robots"]')?.setAttribute("content", route.value.indexable ? "index,follow" : "noindex,follow");
  document.querySelector<HTMLMetaElement>('meta[property="og:url"]')?.setAttribute("content", `${runtimeConfig.publicSiteUrl}${publicPath}`);
  document.querySelector<HTMLMetaElement>('meta[name="twitter:title"]')?.setAttribute("content", route.value.title);
  document.querySelector<HTMLMetaElement>('meta[name="twitter:description"]')?.setAttribute("content", route.value.description);
}

function positionCurrentHash(): void {
  if (!window.location.hash) return;
  const target = document.querySelector<HTMLElement>(window.location.hash);
  if (!target) return;
  const headerHeight = document.querySelector<HTMLElement>(".site-header")?.getBoundingClientRect().height ?? 0;
  window.scrollTo({ top: Math.max(0, window.scrollY + target.getBoundingClientRect().top - headerHeight - 24), behavior: "instant" });
}

function scheduleHashPosition(): void {
  void nextTick(() => requestAnimationFrame(() => requestAnimationFrame(positionCurrentHash)));
}

watch(route, (nextRoute, previousRoute) => {
  updateMetadata();
  if (previousRoute && nextRoute.path !== previousRoute.path && window.location.hash) scheduleHashPosition();
}, { immediate: true });
onMounted(() => {
  document.addEventListener("click", installLinkInterception);
  if (window.location.hash) scheduleHashPosition();
});
</script>

<template>
  <a class="skip-link" href="#main-content">跳到主要内容</a>
  <SiteHeader :current-path="route.path" />
  <main id="main-content" tabindex="-1">
    <component :is="currentComponent" v-bind="routeProps" />
  </main>
  <SiteFooter />
</template>
